/**
 * Calcolo quota GoCicero: versione semplificata del prospetto pagamenti Excel
 * (tmp_gocicero_prospetto_pagamenti_aggiornato.xlsx, foglio "3 - Calcoli").
 *
 * Tariffe e aliquote fisse sono quelle del foglio "2 - Tariffe".
 * Dati dedotti rispetto all'Excel:
 * - numero locali / payout / Connected Account attivi = locali con quota > 0
 * - numero di pagamenti Stripe = 1 (un pagamento cliente per esperienza)
 *
 * Aggiunta rispetto all'Excel: marca da bollo sulla fattura GoCicero se la
 * commissione GoCicero lorda (base + sovrapprezzo) supera la soglia. È un costo
 * fisso come i costi Connect, quindi viene recuperato nel sovrapprezzo.
 */

export const QUOTA_GC_RATES = {
    /** Commissione carta SEE standard, sull'intero pagamento cliente. */
    cardFeePercent: 0.015,
    /** Fee fissa per ogni pagamento Stripe. */
    cardFeeFixed: 0.25,
    /** Connect: funds routing / platform management, sul volume trasferito. */
    connectRoutingPercent: 0.0025,
    /** Connect: payout percentuale, sul volume pagato ai Connected Account. */
    connectPayoutPercent: 0.0025,
    /** Connect: fee fissa per ogni payout. */
    connectPayoutFixed: 0.1,
    /** Connect: costo mensile per ogni Connected Account attivo. */
    connectAccountMonthly: 2,
    /** Aliquota fiscale GoCicero. */
    taxRate: 0.3,
    /** Aliquota inversa (30% / 70%). */
    inverseTaxRate: 30 / 70,
    /** Pagamenti Stripe per esperienza. */
    stripePayments: 1,
    /** Marca da bollo sulla fattura GoCicero. */
    stampDuty: 2,
    /** Soglia oltre la quale si applica la marca da bollo. */
    stampDutyThreshold: 77.47,
} as const;

export interface QuotaGcInput {
    participants: number;
    /** Quota base GoCicero €/persona. */
    gcFeePerPerson: number;
    /** Quota €/persona di ciascun locale. */
    venueFeesPerPerson: number[];
}

export interface QuotaGcResult {
    participants: number;
    activeVenues: number;

    // Prima del sovrapprezzo
    venuesFeePerPerson: number;
    gcFeePerPerson: number;
    basePricePerPerson: number;
    basePrice: number;
    totalToVenues: number;
    gcBaseCommission: number;
    connectRouting: number;
    connectPayoutPercent: number;
    connectPayoutFixed: number;
    connectAccounts: number;
    totalConnectCosts: number;
    netGoCiceroTarget: number;

    // Dopo il sovrapprezzo
    stampDuty: number;
    surcharge: number;
    surchargePerPerson: number;
    totalCustomerPrice: number;
    customerPricePerPerson: number;
    cardFeePercent: number;
    cardFeeFixed: number;
    totalStripeFees: number;
    totalCosts: number;
    gcGross: number;
    netBeforeTaxes: number;
    taxes: number;
    netGoCicero: number;
}

export function calculateQuotaGc(input: QuotaGcInput): QuotaGcResult {
    const r = QUOTA_GC_RATES;
    const participants = Math.max(0, input.participants || 0);
    const gcFeePerPerson = Math.max(0, input.gcFeePerPerson || 0);
    const venueFees = input.venueFeesPerPerson.map(v => Math.max(0, v || 0)).filter(v => v > 0);

    const venuesFeePerPerson = venueFees.reduce((sum, v) => sum + v, 0);
    const activeVenues = venueFees.length;

    const basePricePerPerson = venuesFeePerPerson + gcFeePerPerson;
    const basePrice = basePricePerPerson * participants;
    const totalToVenues = venuesFeePerPerson * participants;
    const gcBaseCommission = gcFeePerPerson * participants;

    const connectRouting = totalToVenues * r.connectRoutingPercent;
    const connectPayoutPercent = totalToVenues * r.connectPayoutPercent;
    const connectPayoutFixed = activeVenues * r.connectPayoutFixed;
    const connectAccounts = activeVenues * r.connectAccountMonthly;
    const totalConnectCosts = connectRouting + connectPayoutPercent + connectPayoutFixed + connectAccounts;

    const cardFeeFixed = r.stripePayments * r.cardFeeFixed;
    const grossUp = 1 + r.inverseTaxRate;
    const surchargeFor = (stampDuty: number) =>
        ((basePrice * r.cardFeePercent + cardFeeFixed + totalConnectCosts + stampDuty) * grossUp) /
        (1 - r.cardFeePercent * grossUp);

    // La soglia si valuta sulla commissione lorda senza bollo: se la supera già,
    // col bollo la supera a maggior ragione, quindi la scelta è coerente.
    const stampDuty = gcBaseCommission + surchargeFor(0) > r.stampDutyThreshold ? r.stampDuty : 0;
    const surcharge = surchargeFor(stampDuty);

    const totalCustomerPrice = basePrice + surcharge;
    const cardFeePercent = totalCustomerPrice * r.cardFeePercent;
    const totalStripeFees = cardFeePercent + cardFeeFixed + totalConnectCosts;

    const gcGross = gcBaseCommission + surcharge;
    const totalCosts = totalStripeFees + stampDuty;
    const netBeforeTaxes = gcGross - totalCosts;
    const taxes = gcGross * r.taxRate;

    return {
        participants,
        activeVenues,
        venuesFeePerPerson,
        gcFeePerPerson,
        basePricePerPerson,
        basePrice,
        totalToVenues,
        gcBaseCommission,
        connectRouting,
        connectPayoutPercent,
        connectPayoutFixed,
        connectAccounts,
        totalConnectCosts,
        netGoCiceroTarget: gcBaseCommission * (1 - r.taxRate),
        stampDuty,
        surcharge,
        surchargePerPerson: participants > 0 ? surcharge / participants : 0,
        totalCustomerPrice,
        customerPricePerPerson: participants > 0 ? totalCustomerPrice / participants : 0,
        cardFeePercent,
        cardFeeFixed,
        totalStripeFees,
        totalCosts,
        gcGross,
        netBeforeTaxes,
        taxes,
        netGoCicero: netBeforeTaxes - taxes,
    };
}
